import type { ReactNode } from "react";

const B = ({ children }: { children: ReactNode }) => (
  <strong className="font-medium text-[var(--color-fg)]">{children}</strong>
);

const verticals: {
  label: string;
  status: string;
  statusColor: string;
  dotColor: string;
  tam: string;
  detail: string;
}[] = [
  {
    label: "Legal agents",
    status: "LIVE",
    statusColor: "text-emerald-500",
    dotColor: "bg-emerald-500",
    tam: "$36B + $20-25B",
    detail: "Paralegal/LPO + legal transactional",
  },
  {
    label: "Fund Ops & Compliance",
    status: "PARTIALLY LIVE",
    statusColor: "text-amber-500",
    dotColor: "bg-amber-500",
    tam: "$30-50B",
    detail: "Compliance review live · KYC/AML in build",
  },
  {
    label: "Accounting agents",
    status: "PLANNED",
    statusColor: "text-[#818cf8]",
    dotColor: "bg-[#818cf8]",
    tam: "$50-80B",
    detail: "Audit prep, tax compliance, bookkeeping",
  },
];

const FlywheelDiagram = () => (
  <svg viewBox="0 0 280 280" className="h-full w-auto" overflow="visible" style={{ transform: "scale(1.3)", overflow: "visible" }}>
    {(() => {
      const cx = 140, cy = 140, r = 88;
      const toRad = (d: number) => (d * Math.PI) / 180;
      const ptX = (a: number, rad = r) => cx + rad * Math.cos(toRad(a));
      const ptY = (a: number, rad = r) => cy + rad * Math.sin(toRad(a));

      const nodes = [
        { a: 270, label: "INGEST", anchor: "middle" as const, dy: -8 },
        { a: 342, label: "BENCHMARK", anchor: "start" as const, dy: 2 },
        { a: 54, label: "EVOLVE", anchor: "start" as const, dy: 5 },
        { a: 126, label: "BETTER\nPRODUCT", anchor: "end" as const, dy: 3 },
        { a: 198, label: "MORE\nPARTNERS", anchor: "end" as const, dy: -2 },
      ];

      const arc = (s: number, e: number) => {
        const x1 = ptX(s), y1 = ptY(s), x2 = ptX(e), y2 = ptY(e);
        const sweep = 1;
        const large = ((e - s + 360) % 360) > 180 ? 1 : 0;
        return `M${x1},${y1} A${r},${r} 0 ${large} ${sweep} ${x2},${y2}`;
      };

      const segments: [number, number][] = [
        [270, 342], [342, 414], [54, 126], [126, 198], [198, 255],
      ];

      const aa = toRad(255), tangent = aa + Math.PI / 2;
      const ax = ptX(255), ay = ptY(255);
      const sz = 7;

      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-border)" strokeWidth="0.5" opacity="0.15" />
          {segments.map(([s, e], i) => (
            <path key={i} d={arc(s, e)} fill="none" stroke="var(--color-accent)" strokeWidth="1.5" />
          ))}
          <polygon
            points={`${ax + sz * Math.cos(tangent)},${ay + sz * Math.sin(tangent)} ${ax + sz * Math.cos(tangent + 2.3)},${ay + sz * Math.sin(tangent + 2.3)} ${ax + sz * Math.cos(tangent - 2.3)},${ay + sz * Math.sin(tangent - 2.3)}`}
            fill="var(--color-accent)"
          />
          <text x={cx} y={cy - 3} textAnchor="middle" fill="var(--color-fg)" fontSize="12" fontFamily="var(--font-heading)" fontWeight="300" opacity="0.35">Data</text>
          <text x={cx} y={cy + 11} textAnchor="middle" fill="var(--color-fg)" fontSize="12" fontFamily="var(--font-heading)" fontWeight="300" opacity="0.35">Flywheel</text>
          {nodes.map((n, i) => {
            const x = ptX(n.a), y = ptY(n.a);
            const lr = r + 16;
            const lx = ptX(n.a, lr), ly = ptY(n.a, lr);
            const lines = n.label.split("\n");
            return (
              <g key={i}>
                <circle cx={x} cy={y} r="4.5" fill="var(--color-accent)" />
                {lines.map((line, li) => (
                  <text
                    key={li}
                    x={lx}
                    y={ly + n.dy + li * 11}
                    textAnchor={n.anchor}
                    fill="var(--color-fg-secondary)"
                    fontSize="10"
                    fontFamily="var(--font-mono)"
                    letterSpacing="0.06em"
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          })}
        </>
      );
    })()}
  </svg>
);

export function V3VerticalsSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[28px] font-light tracking-tight mb-2 font-[family-name:var(--font-heading)]">
        Three verticals. One engine.
      </h2>
      <p className="text-[13px] text-[var(--color-fg-secondary)] font-light leading-relaxed mb-5">
        Skills compound across verticals. Legal sharpens compliance. Compliance sharpens DD. <strong className="font-medium text-[var(--color-fg)]">Every case processed adds capability AND revenue.</strong>
      </p>

      {/* Horizontal flow of 3 verticals */}
      <div className="grid grid-cols-3 gap-4 mb-3">
        {verticals.map((v, i) => (
          <div key={v.label} className="relative">
            <div className="border border-[var(--color-border)] rounded-sm overflow-hidden h-full flex flex-col">
              <div className={`h-[3px] ${v.dotColor}`} />
              <div className="px-4 py-4 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2 h-2 rounded-full ${v.dotColor}`} />
                  <span className={`text-[9px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-widest ${v.statusColor}`}>
                    {v.status}
                  </span>
                </div>
                <h3 className="text-[14px] font-medium font-[family-name:var(--font-heading)] mb-1.5">{v.label}</h3>
                <div className="text-[18px] font-light tracking-tight font-[family-name:var(--font-heading)] mb-1">{v.tam}</div>
                <div className="text-[10px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] uppercase tracking-wider">
                  {v.detail}
                </div>
              </div>
            </div>
            {i < verticals.length - 1 && (
              <div className="absolute top-1/2 right-[-8px] translate-x-1/2 -translate-y-1/2 z-10 text-[var(--color-fg-tertiary)]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Compounding example callout + flywheel side-by-side */}
      <div className="grid grid-cols-3 gap-4 items-center flex-1 min-h-0">
        <div className="col-span-2">
          <div className="text-[10px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-accent)] uppercase tracking-[0.15em] mb-2">
            One agent, three uses
          </div>
          <p className="text-[13px] text-[var(--color-fg-secondary)] leading-relaxed font-light mb-2">
            Our immigration document agent organises client paperwork and cross-references against UKVI rules. The same components handle <B>conveyancing document review</B> and <B>data room due diligence</B>. ~90% shared architecture across these use cases.
          </p>
          <p className="text-[13px] font-medium text-[var(--color-fg)] leading-relaxed">
            Prove it once in legal. Replay in compliance. Replay in accounting.
          </p>
        </div>
        <div className="h-full aspect-square justify-self-center flex items-center justify-center" style={{ transform: "translateY(18px)" }}>
          <FlywheelDiagram />
        </div>
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[10px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] opacity-60">
          Vertical TAM from Sequoia Capital Autopilot Territory analysis, March 2026
        </p>
      </div>
    </div>
  );
}
